import React, { useContext } from 'react';
import { Table, Tag, theme as antTheme, Typography } from 'antd';

import AccountPlanContext from '@totem/components/accountPlan/AccountPlanContext';
import PlanCard from '@totem/components/accountPlan/PlanCard';
import {
  BuildingRevenue,
  ServiceRevenue,
} from '@totem/components/accountPlan/types';
import {
  money,
  moneyExact,
  shortDate,
} from '@totem/components/accountPlan/utilities';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';

type RevenueRow = {
  key: string;
  name: string;
  lines: number | string;
  annual: number;
  oneTime: number;
  termEnd?: string;
  isBuilding: boolean;
  starting?: string;
  children?: RevenueRow[];
};

const serviceRow = (
  building: BuildingRevenue,
  service: ServiceRevenue,
): RevenueRow => ({
  key: `${building.buildingId}-${service.service}`,
  name: `${service.service}${service.yearLabel ? ` (${service.yearLabel})` : ''}`,
  lines: service.status === 'starting' ? 'pend.' : service.lines,
  annual: service.annual,
  oneTime: service.oneTime,
  termEnd: service.termEnd,
  isBuilding: false,
  starting: service.status === 'starting' ? service.startDate : undefined,
});

const RevenueCard = () => {
  const { data } = useContext(AccountPlanContext);
  const { token } = antTheme.useToken();
  if (!data) {
    return null;
  }
  const { revenue } = data.summary;

  const rows: RevenueRow[] = revenue.buildings.map((building) => ({
    key: `b-${building.buildingId || building.name}`,
    name: building.name,
    lines: building.lines,
    annual: building.annual,
    oneTime: 0,
    termEnd: building.termEnd,
    isBuilding: true,
    children: building.services.map((service) => serviceRow(building, service)),
  }));

  const columns = [
    {
      title: 'Building / service',
      dataIndex: 'name',
      render: (name: string, row: RevenueRow) =>
        row.isBuilding ? (
          <Typography.Text strong>{name}</Typography.Text>
        ) : (
          <>
            {name}
            {row.starting && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {' '}
                (starts {shortDate(row.starting)})
              </Typography.Text>
            )}
          </>
        ),
    },
    { title: 'Lines', dataIndex: 'lines', width: 64 },
    {
      title: 'Contracted',
      dataIndex: 'termEnd',
      width: 130,
      render: (termEnd: string | undefined, row: RevenueRow) => {
        if (!termEnd) {
          return '—';
        }
        return row.isBuilding ? (
          <Tag color="green">thru {shortDate(termEnd)}</Tag>
        ) : (
          `thru ${shortDate(termEnd)}`
        );
      },
    },
    {
      title: 'Annual',
      dataIndex: 'annual',
      align: 'right' as const,
      width: 120,
      render: (annual: number, row: RevenueRow) => {
        if (annual === 0 && row.oneTime > 0) {
          return (
            <Typography.Text type="secondary">
              {moneyExact(row.oneTime)} one-time
            </Typography.Text>
          );
        }
        return row.isBuilding ? (
          <Typography.Text strong>{moneyExact(annual)}</Typography.Text>
        ) : (
          moneyExact(annual)
        );
      },
    },
  ];

  return (
    <PlanCard
      variant="live"
      title="Contract & Revenue"
      liveTip={`IB revenue from sales order lines, annualized. The Portal's raw active-line total is ${moneyExact(revenue.recognizedActive)} (partial billing periods).`}
      portalLink="/dashboard/salesOrders"
      extra={`${revenue.activeContracts} active contracts · ~${money(revenue.annualRunRate)} run rate`}
    >
      <IBTable
        size="small"
        rowKey="key"
        columns={columns}
        dataSource={rows}
        pagination={false}
        expandable={{ defaultExpandAllRows: true, indentSize: 12 }}
        onRow={(row: RevenueRow) =>
          row.isBuilding
            ? { style: { background: token.colorFillTertiary } }
            : {}
        }
        locale={{ emptyText: 'No active sales order lines' }}
        summary={() => (
          <Table.Summary>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={3}>
                <Typography.Text strong>
                  {revenue.activeContracts} contracts · {revenue.activeLines}{' '}
                  active lines · {rows.length} buildings
                </Typography.Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                <Typography.Text strong>
                  {moneyExact(revenue.annualRunRate)}
                </Typography.Text>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
      <Typography.Paragraph
        type="secondary"
        style={{ marginTop: 10, marginBottom: 0, fontSize: 12 }}
      >
        Annualized run rate {moneyExact(revenue.annualRunRate)}
        {revenue.startingRunRate > 0 &&
          ` (${moneyExact(revenue.liveRunRate)} live today; +${money(revenue.startingRunRate)} from contracted starts in the next 90 days)`}
        . Contracted backlog of {moneyExact(revenue.contractedBacklog)} (
        {revenue.backlogLines} pending lines
        {revenue.backlogThrough &&
          ` through ${shortDate(revenue.backlogThrough)}`}
        ) is future-period value on signed orders — not pipeline.
        {revenue.nextRenewal &&
          ` Next negotiable event: ${revenue.nextRenewal.buildingName}, ${shortDate(revenue.nextRenewal.date)} (${money(
            revenue.nextRenewal.annual,
          )}/yr).`}
      </Typography.Paragraph>
    </PlanCard>
  );
};

export default RevenueCard;
